import { ITrade } from '@/sdk/entities/models/trade.intraface';
import invariant from 'tiny-invariant';

import { ChainId, BN_ONE, TradeType, BN_ZERO } from '../constants';
import { Currency, ETHER } from './currency';
import { CurrencyAmount } from './fractions/currencyAmount';
import { Fraction } from './fractions/fraction';
import { Percent } from './fractions/percent';
import { Price } from './fractions/price';
import { TokenAmount } from './fractions/tokenAmount';
import { Pair } from './pair';
import { Route } from './route';
import { currencyEquals, Token, WETH } from './token';
import { sortedInsert } from '@/sdk/utils';
import { isValidTrade } from '@/helpers/tradeFilter.helper';
import { PairSourceType } from './pairSource';

export const MAX_HOPS = 3;
export const MIN_HOPS = 1;
export const TRADE_DEFAULT_OPTIONS = {
  maxNumResults: 2,
  maxHops: 3,
  isCrossChainTrade: false,
  enabledLocalPriority: false,
};

/**
 * Returns the percent difference between the mid price and the execution price, i.e. price impact.
 * @param midPrice mid price before the trade
 * @param inputAmount the input amount of the trade
 * @param outputAmount the output amount of the trade
 */
export function computePriceImpact(
  midPrice: Price,
  inputAmount: CurrencyAmount,
  outputAmount: CurrencyAmount,
): Percent {
  // IN / OUT
  const marketPriceOut = midPrice.invert().raw;
  // decimals = IN_decimals - OUT_decimals
  const paidPriceOut = new Price(
    outputAmount.currency,
    inputAmount.currency,
    outputAmount.raw,
    inputAmount.raw,
  ).raw;

  if (paidPriceOut.greaterThan(marketPriceOut)) {
    const diffPrice = paidPriceOut.subtract(marketPriceOut);
    const priceImpact = diffPrice.divide(marketPriceOut);
    return new Percent(priceImpact.numerator, priceImpact.denominator);
  }

  const diffPrice = marketPriceOut.subtract(paidPriceOut);
  const priceImpact = diffPrice.divide(paidPriceOut);
  return new Percent(priceImpact.numerator, priceImpact.denominator);
}

/**
 * Returns the percent difference between the mid price and the execution price, i.e. price impact.
 * @param midPrice mid price before the trade
 * @param inputAmount the input amount of the trade
 * @param outputAmount the output amount of the trade
 */
export function computeSlippage(
  midPrice: Price,
  inputAmount: CurrencyAmount,
  outputAmount: CurrencyAmount,
): Percent {
  const exactQuote = midPrice.raw.multiply(inputAmount.raw);
  // calculate slippage := (exactQuote - outputAmount) / exactQuote
  const slippage = exactQuote.subtract(outputAmount.raw).divide(exactQuote);
  return new Percent(slippage.numerator, slippage.denominator);
}

// minimal interface so the input output comparator may be shared across types
interface InputOutput {
  readonly inputAmount: CurrencyAmount;
  readonly outputAmount: CurrencyAmount;
}

// comparator function that allows sorting trades by their output amounts, in decreasing order, and then input amounts
// in increasing order. i.e. the best trades have the most outputs for the least inputs and are sorted first
export function inputOutputComparator(a: InputOutput, b: InputOutput): number {
  // must have same input and output token for comparison
  invariant(currencyEquals(a.inputAmount.currency, b.inputAmount.currency), 'INPUT_CURRENCY');
  invariant(currencyEquals(a.outputAmount.currency, b.outputAmount.currency), 'OUTPUT_CURRENCY');
  if (a.outputAmount.equalTo(b.outputAmount)) {
    if (a.inputAmount.equalTo(b.inputAmount)) {
      return 0;
    }
    // trade A requires less input than trade B, so A should come first
    if (a.inputAmount.lessThan(b.inputAmount)) {
      return -1;
    } else {
      return 1;
    }
  } else {
    // tradeA has less output than trade B, so should come second
    if (a.outputAmount.lessThan(b.outputAmount)) {
      return 1;
    } else {
      return -1;
    }
  }
}

// when local swap priority true then local trade should be first
export function tradeComparatorWithLocalPriority(a: Trade, b: Trade) {
  if (a.isLocal() && b.isCrossChain()) {
    return -1;
  }

  if (a.isCrossChain() && b.isLocal()) {
    return 1;
  }

  return tradeComparator(a, b);
}

// extension of the input output comparator that also considers other dimensions of the trade in ranking them
export function tradeComparator(a: Trade, b: Trade) {
  const ioComp = inputOutputComparator(a, b);
  if (ioComp !== 0) {
    return ioComp;
  }

  // consider lowest slippage next, since these are less likely to fail
  if (a.priceImpact.lessThan(b.priceImpact)) {
    return -1;
  } else if (a.priceImpact.greaterThan(b.priceImpact)) {
    return 1;
  }

  // finally consider the number of hops since each hop costs gas
  return a.route.path.length - b.route.path.length;
}

function findTradeComparator(enabledLocalPriority: boolean) {
  return enabledLocalPriority ? tradeComparatorWithLocalPriority : tradeComparator;
}

export interface BestTradeOptions {
  // how many results to return
  maxNumResults?: number;
  // the maximum number of hops a trade should contain
  maxHops?: number;
  // In and Out tokens are from different chain
  isCrossChainTrade?: boolean;
  // local trade is more priority in best trades
  enabledLocalPriority?: boolean;
}

/**
 * Given a currency amount and a chain ID, returns the equivalent representation as the token amount.
 * In other words, if the currency is ETHER, returns the WETH token amount for the given chain. Otherwise, returns
 * the input currency amount.
 */
function wrappedAmount(currencyAmount: CurrencyAmount, chainId: ChainId): TokenAmount {
  if (currencyAmount instanceof TokenAmount) return currencyAmount;
  if (currencyAmount.currency === ETHER) return new TokenAmount(WETH[chainId], currencyAmount.raw);
  invariant(false, 'CURRENCY AMOUNT');
}

function wrappedCurrency(currency: Currency, chainId: ChainId): Token {
  if (currency instanceof Token) return currency;
  if (currency === ETHER) return WETH[chainId];
  invariant(false, 'CURRENCY TOKEN');
}

/**
 * Represents a trade executed against a list of pairs.
 * Does not account for slippage, i.e. trades that front run this trade and move the price.
 */
export class Trade implements ITrade {
  /**
   * The route of the trade, i.e. which pairs the trade goes through.
   */
  public readonly route: Route;
  /**
   * The type of the trade, either exact in or exact out.
   */
  public readonly tradeType: TradeType;
  /**
   * The input amount for the trade assuming no slippage.
   */
  public readonly inputAmount: CurrencyAmount;
  /**
   * The output amount for the trade assuming no slippage.
   */
  public readonly outputAmount: CurrencyAmount;
  /**
   * The price expressed in terms of output amount/input amount.
   */
  public readonly executionPrice: Price;
  /**
   * The mid price after the trade executes assuming no slippage.
   */
  public readonly nextMidPrice: Price;
  /**
   * The percent difference between the mid price before the trade and the trade execution price.
   */
  public readonly priceImpact: Percent;
  /**
   * The token out from user
   */
  public readonly tokenOut: Token;

  /**
   * Constructs an exact in trade with the given amount in and route
   * @param route route of the exact in trade
   * @param amountIn the amount being passed in
   */
  public static exactIn(route: Route, amountIn: CurrencyAmount, tokenOut: Token): Trade {
    return new Trade(route, amountIn, tokenOut, TradeType.EXACT_INPUT);
  }

  /**
   * Constructs an exact out trade with the given amount out and route
   * @param route route of the exact out trade
   * @param amountOut the amount returned by the trade
   */
  public static exactOut(route: Route, amountOut: CurrencyAmount, tokenOut: Token): Trade {
    return new Trade(route, amountOut, tokenOut, TradeType.EXACT_OUTPUT);
  }

  public constructor(route: Route, amount: CurrencyAmount, tokenOut: Token, tradeType: TradeType) {
    const amounts: TokenAmount[] = new Array(route.path.length);
    const nextPairs: Pair[] = new Array(route.pairs.length);
    if (tradeType === TradeType.EXACT_INPUT) {
      invariant(currencyEquals(amount.currency, route.input), 'INPUT');
      amounts[0] = wrappedAmount(amount, route.chainId);
      for (let i = 0; i < route.path.length - 1; i++) {
        const pair = route.pairs[i];
        const [outputAmount, nextPair] = pair.getOutputAmount(amounts[i]);
        amounts[i + 1] = outputAmount;
        nextPairs[i] = nextPair;
      }
    } else {
      invariant(currencyEquals(amount.currency, route.output), 'OUTPUT');
      amounts[amounts.length - 1] = wrappedAmount(amount, route.chainId);
      for (let i = route.path.length - 1; i > 0; i--) {
        const pair = route.pairs[i - 1];
        const [inputAmount, nextPair] = pair.getInputAmount(amounts[i]);
        amounts[i - 1] = inputAmount;
        nextPairs[i - 1] = nextPair;
      }
    }

    this.route = route;
    this.tradeType = tradeType;
    this.tokenOut = tokenOut;
    this.inputAmount =
      tradeType === TradeType.EXACT_INPUT
        ? amount
        : route.input === ETHER
        ? CurrencyAmount.ether(amounts[0].raw)
        : amounts[0];
    this.outputAmount =
      tradeType === TradeType.EXACT_OUTPUT
        ? amount
        : route.output === ETHER
        ? CurrencyAmount.ether(amounts[amounts.length - 1].raw)
        : amounts[amounts.length - 1];
    this.executionPrice = new Price(
      this.inputAmount.currency,
      this.outputAmount.currency,
      this.inputAmount.raw,
      this.outputAmount.raw,
    );
    this.nextMidPrice = Price.fromRoute(new Route(nextPairs, route.input));
    this.priceImpact = computePriceImpact(route.midPrice, this.inputAmount, this.outputAmount);
  }

  public isCrossChain() {
    const pairs = this.route.pairs;
    const crossChainPairsCount = pairs.filter(
      pair => pair.pairSource.type === PairSourceType.CROSSCHAIN_PORTFOLIO,
    ).length;
    const lastPair: Pair = pairs[pairs.length - 1];

    return (
      crossChainPairsCount === 1 && lastPair.pairSource.type === PairSourceType.CROSSCHAIN_PORTFOLIO
    );
  }

  public isLocal() {
    return !this.isCrossChain();
  }

  public getDestinationChainId(): ChainId | 0 {
    return this.isCrossChain() ? this.tokenOut.chainId : 0;
  }

  /**
   * Get the minimum amount that must be received from this trade for the given slippage tolerance
   * @param slippageTolerance tolerance of unfavorable slippage from the execution price of this trade
   */
  public minimumAmountOut(slippageTolerance: Percent): CurrencyAmount {
    invariant(!slippageTolerance.lessThan(BN_ZERO), 'SLIPPAGE_TOLERANCE');
    if (this.tradeType === TradeType.EXACT_OUTPUT) {
      return this.outputAmount;
    } else {
      const slippageAdjustedAmountOut = new Fraction(BN_ONE)
        .add(slippageTolerance)
        .invert()
        .multiply(this.outputAmount.raw).quotient;
      return this.outputAmount instanceof TokenAmount
        ? new TokenAmount(this.outputAmount.token, slippageAdjustedAmountOut)
        : CurrencyAmount.ether(slippageAdjustedAmountOut);
    }
  }

  /**
   * Get the maximum amount in that can be spent via this trade for the given slippage tolerance
   * @param slippageTolerance tolerance of unfavorable slippage from the execution price of this trade
   */
  public maximumAmountIn(slippageTolerance: Percent): CurrencyAmount {
    invariant(!slippageTolerance.lessThan(BN_ZERO), 'SLIPPAGE_TOLERANCE');
    if (this.tradeType === TradeType.EXACT_INPUT) {
      return this.inputAmount;
    } else {
      const slippageAdjustedAmountIn = new Fraction(BN_ONE)
        .add(slippageTolerance)
        .multiply(this.inputAmount.raw).quotient;
      return this.inputAmount instanceof TokenAmount
        ? new TokenAmount(this.inputAmount.token, slippageAdjustedAmountIn)
        : CurrencyAmount.ether(slippageAdjustedAmountIn);
    }
  }

  /**
   * Given a list of pairs, and a fixed amount in, returns the top `maxNumResults` trades that go from an input token
   * amount to an output token, making at most `maxHops` hops.
   * Note this does not consider aggregation, as routes are linear. It's possible a better route exists by splitting
   * the amount in among multiple routes.
   * @param pairs the pairs to consider in finding the best trade
   * @param currencyAmountIn exact amount of input currency to spend
   * @param currencyOut the desired currency out
   * @param maxNumResults maximum number of results to return
   * @param maxHops maximum number of hops a returned trade can make, e.g. 1 hop goes through a single pair
   * @param isCrossChainTrade currencyAmountIn and currencyOut are from different chain
   * @param enabledLocalPriority local trade is more priority in best trades
   * @param currentPairs used in recursion; the current list of pairs
   * @param originalAmountIn used in recursion; the original value of the currencyAmountIn parameter
   * @param bestTrades used in recursion; the current list of best trades
   */
  public static bestTradeExactIn(
    pairs: Pair[],
    currencyAmountIn: CurrencyAmount,
    currencyOut: Currency,
    {
      maxNumResults = 3,
      maxHops = 3,
      isCrossChainTrade = false,
      enabledLocalPriority = false,
    }: BestTradeOptions = {},
    // used in recursion.
    currentPairs: Pair[] = [],
    originalAmountIn: CurrencyAmount = currencyAmountIn,
    bestTrades: Trade[] = [],
  ): Trade[] {
    invariant(pairs.length > 0, 'PAIRS');
    invariant(maxHops > 0, 'MAX_HOPS');
    invariant(
      originalAmountIn === currencyAmountIn || currentPairs.length > 0,
      'INVALID_RECURSION',
    );
    const chainId: ChainId | undefined =
      currencyAmountIn instanceof TokenAmount
        ? currencyAmountIn.token.chainId
        : currencyOut instanceof Token
        ? currencyOut.chainId
        : undefined;
    invariant(chainId !== undefined, 'CHAIN_ID');

    const amountIn = wrappedAmount(currencyAmountIn, chainId);
    const tokenOut = wrappedCurrency(currencyOut, chainId);

    loggingLookingForPairWith(maxHops, amountIn);

    for (let i = 0; i < pairs.length; i++) {
      const pair = pairs[i];

      loggingLookPair(maxHops, i, pair);

      // pair irrelevant
      if (!pair.token0.equals(amountIn.token)) continue;
      if (pair.reserve0.equalTo(BN_ZERO) || pair.reserve1.equalTo(BN_ZERO)) continue;

      let amountOut: TokenAmount;
      try {
        [amountOut] = pair.getOutputAmount(amountIn);
      } catch (error) {
        // not enough liquidity in this pair
        if (error.isInsufficientInputAmountError) {
          loggingInsufficientLiquidityInPair(maxHops, amountIn, pair);
          continue;
        }
        throw error;
      }
      // we have arrived at the output token, so this is the final trade of one of the paths
      if (this.isTradeReady(pair, tokenOut, amountOut)) {
        const trade = new Trade(
          new Route([...currentPairs, pair], originalAmountIn.currency, currencyOut),
          originalAmountIn,
          tokenOut,
          TradeType.EXACT_INPUT,
        );

        loggingFoundTrade(maxHops, originalAmountIn, tokenOut, trade);

        if (isValidTrade(trade, isCrossChainTrade)) {
          sortedInsert(bestTrades, trade, maxNumResults, findTradeComparator(enabledLocalPriority));
        }
      } else if (maxHops > 1 && pairs.length > 1) {
        const pairsExcludingThisPair = pairs.slice(0, i).concat(pairs.slice(i + 1, pairs.length));

        loggingFoundPair(maxHops, pair);

        // otherwise, consider all the other paths that lead from this token as long as we have not exceeded maxHops
        Trade.bestTradeExactIn(
          pairsExcludingThisPair,
          amountOut,
          currencyOut,
          {
            maxNumResults,
            maxHops: maxHops - 1,
            isCrossChainTrade,
            enabledLocalPriority,
          },
          [...currentPairs, pair],
          originalAmountIn,
          bestTrades,
        );
      }
    }

    loggingBackLookingPairWith(maxHops, amountIn);
    return bestTrades;
  }

  /**
   * similar to the above method but instead targets a fixed output amount
   * given a list of pairs, and a fixed amount out, returns the top `maxNumResults` trades that go from an input token
   * to an output token amount, making at most `maxHops` hops
   * note this does not consider aggregation, as routes are linear. it's possible a better route exists by splitting
   * the amount in among multiple routes.
   * @param pairs the pairs to consider in finding the best trade
   * @param currencyIn the currency to spend
   * @param currencyAmountOut the exact amount of currency out
   * @param maxNumResults maximum number of results to return
   * @param maxHops maximum number of hops a returned trade can make, e.g. 1 hop goes through a single pair
   * @param isCrossChainTrade currencyIn and currencyAmountOut are from different chain
   * @param enabledLocalPriority local trade is more priority in best trades
   * @param currentPairs used in recursion; the current list of pairs
   * @param originalAmountOut used in recursion; the original value of the currencyAmountOut parameter
   * @param bestTrades used in recursion; the current list of best trades
   */
  public static bestTradeExactOut(
    pairs: Pair[],
    currencyIn: Currency,
    currencyAmountOut: CurrencyAmount,
    {
      maxNumResults = 3,
      maxHops = 3,
      isCrossChainTrade = false,
      enabledLocalPriority = false,
    }: BestTradeOptions = {},
    // used in recursion.
    currentPairs: Pair[] = [],
    originalAmountOut: CurrencyAmount = currencyAmountOut,
    bestTrades: Trade[] = [],
  ): Trade[] {
    invariant(pairs.length > 0, 'PAIRS');
    invariant(maxHops > 0, 'MAX_HOPS');
    invariant(
      originalAmountOut === currencyAmountOut || currentPairs.length > 0,
      'INVALID_RECURSION',
    );
    const chainId: ChainId | undefined =
      currencyAmountOut instanceof TokenAmount
        ? currencyAmountOut.token.chainId
        : currencyIn instanceof Token
        ? currencyIn.chainId
        : undefined;
    invariant(chainId !== undefined, 'CHAIN_ID');

    const amountOut = wrappedAmount(currencyAmountOut, chainId);
    const tokenIn = wrappedCurrency(currencyIn, chainId);

    loggingLookingForPairWith(maxHops, amountOut);

    for (let i = 0; i < pairs.length; i++) {
      const pair = pairs[i];

      loggingLookPair(maxHops, i, pair);

      // pair irrelevant
      if (!pair.token1.equals(amountOut.token)) continue;
      if (pair.reserve0.equalTo(BN_ZERO) || pair.reserve1.equalTo(BN_ZERO)) continue;

      let amountIn: TokenAmount;
      try {
        [amountIn] = pair.getInputAmount(amountOut);
      } catch (error) {
        // not enough liquidity in this pair
        if (error.isInsufficientReservesError) {
          loggingInsufficientLiquidityInPair(maxHops, amountOut, pair);
          continue;
        }
        throw error;
      }
      // we have arrived at the input token, so this is the first trade of one of the paths
      if (this.isTradeReady(pair, tokenIn, amountIn)) {
        const originTokenOut = wrappedAmount(originalAmountOut, chainId).token;

        const trade = new Trade(
          new Route([pair, ...currentPairs], currencyIn, originalAmountOut.currency),
          originalAmountOut,
          originTokenOut,
          TradeType.EXACT_OUTPUT,
        );

        loggingFoundTrade(maxHops, originalAmountOut, tokenIn, trade);

        if (isValidTrade(trade, isCrossChainTrade)) {
          sortedInsert(bestTrades, trade, maxNumResults, findTradeComparator(enabledLocalPriority));
        }
      } else if (maxHops > 1 && pairs.length > 1) {
        const pairsExcludingThisPair = pairs.slice(0, i).concat(pairs.slice(i + 1, pairs.length));

        loggingFoundPair(maxHops, pair);

        // otherwise, consider all the other paths that arrive at this token as long as we have not exceeded maxHops
        Trade.bestTradeExactOut(
          pairsExcludingThisPair,
          currencyIn,
          amountIn,
          {
            maxNumResults,
            maxHops: maxHops - 1,
            isCrossChainTrade,
            enabledLocalPriority,
          },
          [pair, ...currentPairs],
          originalAmountOut,
          bestTrades,
        );
      }
    }

    loggingBackLookingPairWith(maxHops, amountOut);
    return bestTrades;
  }

  private static isTradeReady(pair: Pair, tokenTo: Token, amountTo: TokenAmount) {
    if (pair.pairSource.type === PairSourceType.CROSSCHAIN_PORTFOLIO) {
      return amountTo.token.equalsBySymbol(tokenTo);
    }

    return amountTo.token.equals(tokenTo);
  }
}

// DEBUG

function isLoggingDisabled() {
  return !window['BLUESHIFT_DEBUG'].LOOKING_FOR_TRADE;
}

function treeView(maxHops: number) {
  const deep = MAX_HOPS - maxHops;
  const PAD = 3;

  return ''.padEnd(deep * PAD);
}

function loggingLookingForPairWith(currentMaxHops: number, amount: TokenAmount) {
  if (isLoggingDisabled()) return;

  console.log(
    `${treeView(currentMaxHops)} [START] Now looking for pair with : ${amount.token.symbol}`,
  );
}

function loggingLookPair(currentMaxHops: number, idx: number, pair: Pair) {
  if (isLoggingDisabled()) return;

  console.log(
    `${treeView(currentMaxHops)} [LOOK] maxHops = ${currentMaxHops} | index = ${idx} | pair = ${
      pair.token0.symbol
    }-${pair.token1.symbol}`,
  );
}

function loggingFoundPair(currentMaxHops: number, pair: Pair) {
  if (isLoggingDisabled()) return;

  console.log(
    `${treeView(currentMaxHops)} [FOUND -> NEXT] Found pair : ${pair.token0.symbol}-${
      pair.token1.symbol
    }`,
  );
}

function loggingInsufficientLiquidityInPair(
  currentMaxHops: number,
  amount: TokenAmount,
  pair: Pair,
) {
  if (isLoggingDisabled()) return;

  console.log(
    `${treeView(currentMaxHops)} [ERROR] maxHops = ${currentMaxHops} | input = ${amount.toFixed(
      3,
    )} | Not enough liquidity in pair ${pair.token0.symbol}-${pair.token1.symbol}`,
  );
}

function loggingBackLookingPairWith(currentMaxHops: number, amount: TokenAmount) {
  if (isLoggingDisabled()) return;

  console.log(
    `${treeView(
      currentMaxHops,
    )} [ <- BACK] maxHops = ${currentMaxHops} | Finish looking for pair with ${
      amount.token.symbol
    }`,
  );
}

function loggingFoundTrade(
  currentMaxHops: number,
  amount: CurrencyAmount,
  token: Token,
  trade: Trade,
) {
  if (isLoggingDisabled()) return;

  console.log(
    `${treeView(currentMaxHops)} [TRADE] Found trade ${amount.currency.symbol}-${
      token.symbol
    } | maxHops = ${currentMaxHops} | ${trade.route.path.map(
      (t, i) => `${i > 0 ? '-' : ''}${t.symbol}`,
    )}`,
  );
}
